import React from 'react';
import { Calendar, Clock, TrendingUp, AlertCircle } from 'lucide-react';
import { CitizenshipEligibility } from '../types';
import { formatDaysToYearsMonthsDays, calculateProgress } from '../utils/residencyCalculator';

interface CountdownTimerProps {
  eligibility: CitizenshipEligibility;
}

export const CountdownTimer: React.FC<CountdownTimerProps> = ({ eligibility }) => {
  const progress = calculateProgress(eligibility.daysCompleted, eligibility.daysRequired);
  const progressPercentage = Math.min(100, progress);
  
  const formatEligibilityDate = (dateString: string | null) => {
    if (!dateString) return 'Not calculated';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-CA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="card">
      <div style={{ textAlign: 'center', marginBottom: 'var(--sd-space-6)' }}>
        <div className={`badge ${eligibility.isEligible ? 'badge-positive' : 'badge-neutral'}`} style={{ marginBottom: 'var(--sd-space-3)' }}>
          {eligibility.isEligible ? (
            <>
              <TrendingUp size={16} />
              Eligible to Apply
            </>
          ) : (
            <>
              <Clock size={16} />
              In Progress
            </>
          )}
        </div>
        <h2 style={{ 
          margin: 0, 
          fontSize: 'var(--sd-font-size-display-md)',
          fontWeight: 'var(--sd-font-weight-bold)',
          color: 'var(--sd-color-text-primary)',
          marginBottom: 'var(--sd-space-2)'
        }}>
          Canadian Citizenship Progress
        </h2>
        <p style={{ 
          margin: 0,
          color: 'var(--sd-color-text-secondary)',
          fontSize: 'var(--sd-font-size-md)'
        }}>
          {eligibility.isEligible 
            ? 'You have met the physical presence requirement and can apply for citizenship!'
            : `${eligibility.daysRemaining} days remaining until you can apply`
          }
        </p>
      </div>

      {/* Progress Bar */}
      <div style={{ marginBottom: 'var(--sd-space-8)' }}>
        <div style={{ 
          textAlign: 'center',
          marginBottom: 'var(--sd-space-4)'
        }}>
          <div className="stat-hero" style={{
            fontWeight: 'var(--sd-font-weight-bold)',
            color: eligibility.isEligible 
              ? 'var(--sd-color-status-positive)' 
              : 'var(--sd-color-brand-primary)',
            marginBottom: 'var(--sd-space-2)'
          }}>
            {progressPercentage}%
          </div>
          <div style={{
            fontSize: 'var(--sd-font-size-md)',
            color: 'var(--sd-color-text-secondary)',
            fontWeight: 'var(--sd-font-weight-medium)'
          }}>
            Physical Presence Complete
          </div>
        </div>
        
        <div style={{
          width: '100%',
          height: '12px',
          backgroundColor: 'var(--sd-color-bg-muted)',
          borderRadius: 'var(--sd-radius-pill)',
          overflow: 'hidden',
          margin: '0 auto',
          maxWidth: '400px'
        }}>
          <div style={{
            width: `${progressPercentage}%`,
            height: '100%',
            background: eligibility.isEligible 
              ? 'linear-gradient(90deg, var(--sd-color-status-positive), #4ADE80)' 
              : 'linear-gradient(90deg, var(--sd-color-brand-primary), var(--sd-color-brand-secondary))',
            borderRadius: 'var(--sd-radius-pill)',
            transition: 'width 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
          }} />
        </div>
      </div>

      {/* Eligibility Date */}
      <div style={{
        padding: 'var(--sd-space-4)',
        backgroundColor: eligibility.isEligible 
          ? 'var(--sd-color-bg-tint-blue)' 
          : 'var(--sd-color-bg-muted)',
        borderRadius: 'var(--sd-radius-md)',
        border: `1px solid ${eligibility.isEligible 
          ? 'var(--sd-color-status-positive)' 
          : 'var(--sd-color-border-subtle)'}`,
        marginBottom: 'var(--sd-space-4)'
      }}>
        <div className="flex" style={{ alignItems: 'flex-start' }}>
          <Calendar 
            size={20} 
            style={{ 
              color: eligibility.isEligible 
                ? 'var(--sd-color-status-positive)' 
                : 'var(--sd-color-text-tertiary)',
              marginTop: '2px',
              flexShrink: 0
            }} 
          />
          <div style={{ marginLeft: 'var(--sd-space-3)' }}>
            <div style={{
              fontSize: 'var(--sd-font-size-md)',
              fontWeight: 'var(--sd-font-weight-semibold)',
              color: 'var(--sd-color-text-primary)',
              marginBottom: '2px'
            }}>
              {eligibility.isEligible ? 'You can apply now!' : 'Estimated Eligibility Date'}
            </div>
            <div style={{
              fontSize: 'var(--sd-font-size-sm)',
              color: 'var(--sd-color-text-secondary)'
            }}>
              {eligibility.isEligible 
                ? 'You have met the physical presence requirement'
                : formatEligibilityDate(eligibility.eligibilityDate)
              }
            </div>
          </div>
        </div>
      </div>

      {/* Time Breakdown */}
      <div style={{
        padding: 'var(--sd-space-4)',
        backgroundColor: 'var(--sd-color-bg-muted)',
        borderRadius: 'var(--sd-radius-md)',
        marginBottom: 'var(--sd-space-4)'
      }}>
        <h4 style={{
          margin: '0 0 var(--sd-space-3) 0',
          fontSize: 'var(--sd-font-size-md)',
          fontWeight: 'var(--sd-font-weight-semibold)',
          color: 'var(--sd-color-text-primary)'
        }}>
          Time Breakdown
        </h4>
        
        <div className="stack" style={{ gap: 'var(--sd-space-2)' }}>
          <div className="flex-between row-metric">
            <span style={{ fontSize: 'var(--sd-font-size-sm)', color: 'var(--sd-color-text-secondary)' }}>
              Permanent Residency Days
            </span>
            <span style={{ fontSize: 'var(--sd-font-size-sm)', fontWeight: 'var(--sd-font-weight-medium)' }}>
              {eligibility.permanentResidencyDays}
            </span>
          </div>
          
          <div className="flex-between row-metric">
            <span style={{ fontSize: 'var(--sd-font-size-sm)', color: 'var(--sd-color-text-secondary)' }}>
              Temporary Residency Days (½ credit)
            </span>
            <span style={{ fontSize: 'var(--sd-font-size-sm)', fontWeight: 'var(--sd-font-weight-medium)' }}>
              {eligibility.temporaryResidencyDays}
            </span>
          </div>
          
          <div style={{ height: '1px', backgroundColor: 'var(--sd-color-border-subtle)', margin: 'var(--sd-space-1) 0' }} />
          
          <div className="flex-between row-metric">
            <span style={{ fontSize: 'var(--sd-font-size-sm)', fontWeight: 'var(--sd-font-weight-semibold)' }}>
              Total Qualifying Days
            </span>
            <span style={{ 
              fontSize: 'var(--sd-font-size-sm)', 
              fontWeight: 'var(--sd-font-weight-bold)',
              color: 'var(--sd-color-brand-primary)'
            }}>
              {eligibility.daysCompleted}
            </span>
          </div>
        </div>
      </div>

      {/* Warning for incomplete data */}
      {!eligibility.isEligible && eligibility.daysRemaining > 0 && (
        <div style={{
          padding: 'var(--sd-space-3)',
          backgroundColor: 'rgba(245, 158, 11, 0.1)',
          border: '1px solid var(--sd-color-status-warning)',
          borderRadius: 'var(--sd-radius-md)',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 'var(--sd-space-2)'
        }}>
          <AlertCircle size={16} style={{ color: 'var(--sd-color-status-warning)', marginTop: '2px', flexShrink: 0 }} />
          <div>
            <div style={{
              fontSize: 'var(--sd-font-size-sm)',
              fontWeight: 'var(--sd-font-weight-medium)',
              color: 'var(--sd-color-text-primary)',
              marginBottom: '2px'
            }}>
              Time Remaining: {formatDaysToYearsMonthsDays(eligibility.daysRemaining)}
            </div>
            <div style={{
              fontSize: 'var(--sd-font-size-xs)',
              color: 'var(--sd-color-text-secondary)'
            }}>
              This calculation is based on your current data. Add your trips to get a more accurate estimate.
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
